import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, MapPin, Filter, Star, Award, Utensils, Clock } from 'lucide-react';

const mockRestaurants = [
  {
    id: 1,
    name: 'Sakura Sushi',
    city: 'Milano',
    cuisine: 'Giapponese',
    priceRange: 'alto',
    bushiScore: 87,
    pillars: {
      '義': 5, '勇': 4, '仁': 5, '礼': 5, '誠': 4, '忠義': 5, '清': 5
    },
    badges: ['Esperto Sushi', 'Sostenibilità'],
    reviews: 23,
    avgPrice: 65,
    image: '/api/placeholder/300/200'
  },
  {
    id: 2,
    name: 'Osteria del Borgo',
    city: 'Roma',
    cuisine: 'Italiana',
    priceRange: 'medio',
    bushiScore: 92,
    pillars: {
      '義': 5, '勇': 4, '仁': 5, '礼': 5, '誠': 5, '忠義': 5, '清': 4
    },
    badges: ['Tradizione', 'Accessibilità'],
    reviews: 45,
    avgPrice: 35,
    image: '/api/placeholder/300/200'
  },
  {
    id: 3,
    name: 'Fusion Lab',
    city: 'Torino',
    cuisine: 'Fusion',
    priceRange: 'alto',
    bushiScore: 78,
    pillars: {
      '義': 4, '勇': 5, '仁': 4, '礼': 4, '誠': 4, '忠義': 4, '清': 4
    },
    badges: ['Innovazione'],
    reviews: 18,
    avgPrice: 75,
    image: '/api/placeholder/300/200'
  },
  {
    id: 4,
    name: 'Trattoria Nonna',
    city: 'Napoli',
    cuisine: 'Italiana',
    priceRange: 'economico',
    bushiScore: 85,
    pillars: {
      '義': 5, '勇': 3, '仁': 5, '礼': 4, '誠': 5, '忠義': 5, '清': 4
    },
    badges: ['Tradizione', 'Famiglia'],
    reviews: 67,
    avgPrice: 22,
    image: '/api/placeholder/300/200'
  }
];

const pillarsData = [
  { kanji: '義', name: 'Giustizia' },
  { kanji: '勇', name: 'Coraggio' },
  { kanji: '仁', name: 'Compassione' },
  { kanji: '礼', name: 'Rispetto' },
  { kanji: '誠', name: 'Onestà' },
  { kanji: '忠義', name: 'Devozione' },
  { kanji: '清', name: 'Purezza' }
];

const cities = ['Tutte', 'Milano', 'Roma', 'Torino', 'Napoli', 'Firenze', 'Bologna'];
const cuisines = ['Tutte', 'Italiana', 'Giapponese', 'Fusion', 'Francese', 'Cinese', 'Indiana'];
const priceRanges = ['Tutte', 'economico', 'medio', 'alto', 'lusso'];

export default function SearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('Tutte');
  const [selectedCuisine, setSelectedCuisine] = useState('Tutte');
  const [selectedPriceRange, setSelectedPriceRange] = useState('Tutte');
  const [bushiScoreRange, setBushiScoreRange] = useState([0, 100]);
  const [selectedPillars, setSelectedPillars] = useState<string[]>([]);
  const [selectedBadges, setSelectedBadges] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('bushiScore');

  const availableBadges = ['Esperto Sushi', 'Sostenibilità', 'Tradizione', 'Accessibilità', 'Innovazione', 'Famiglia'];

  const handlePillarToggle = (pillar: string) => {
    setSelectedPillars(prev => 
      prev.includes(pillar) 
        ? prev.filter(p => p !== pillar)
        : [...prev, pillar]
    );
  };

  const handleBadgeToggle = (badge: string) => {
    setSelectedBadges(prev => 
      prev.includes(badge) 
        ? prev.filter(b => b !== badge)
        : [...prev, badge]
    );
  };

  const filteredRestaurants = mockRestaurants.filter(restaurant => {
    // Filtro per query di ricerca
    if (searchQuery && !restaurant.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Filtro per città
    if (selectedCity !== 'Tutte' && restaurant.city !== selectedCity) {
      return false;
    }
    
    // Filtro per cucina
    if (selectedCuisine !== 'Tutte' && restaurant.cuisine !== selectedCuisine) {
      return false;
    }
    
    // Filtro per fascia di prezzo
    if (selectedPriceRange !== 'Tutte' && restaurant.priceRange !== selectedPriceRange) {
      return false;
    }
    
    // Filtro per BushiScore
    if (restaurant.bushiScore < bushiScoreRange[0] || restaurant.bushiScore > bushiScoreRange[1]) {
      return false;
    }
    
    // Filtro per pilastri (almeno uno deve essere >= 4)
    if (selectedPillars.length > 0) {
      const hasRequiredPillars = selectedPillars.some(pillar => 
        restaurant.pillars[pillar as keyof typeof restaurant.pillars] >= 4
      );
      if (!hasRequiredPillars) return false;
    }
    
    // Filtro per badge
    if (selectedBadges.length > 0) {
      const hasRequiredBadges = selectedBadges.some(badge => 
        restaurant.badges.includes(badge)
      );
      if (!hasRequiredBadges) return false;
    }
    
    return true;
  });

  const sortedRestaurants = [...filteredRestaurants].sort((a, b) => {
    switch (sortBy) {
      case 'bushiScore':
        return b.bushiScore - a.bushiScore;
      case 'reviews':
        return b.reviews - a.reviews;
      case 'price':
        return a.avgPrice - b.avgPrice;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  const getBushiScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 80) return 'text-blue-500';
    if (score >= 70) return 'text-yellow-500';
    if (score >= 60) return 'text-orange-500';
    return 'text-red-500';
  };

  const getPriceRangeSymbol = (range: string) => {
    switch (range) {
      case 'economico': return '€';
      case 'medio': return '€€';
      case 'alto': return '€€€';
      case 'lusso': return '€€€€';
      default: return '€';
    }
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-bushido text-4xl font-bold mb-4">
            Ricerca & <span className="gold-accent">Discovery</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Trova locali basandoti sui Pilastri etici e badge verificati. 
            Scopri ristoranti che condividono i tuoi valori culinari.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filtri Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Filter className="mr-2 h-5 w-5" />
                  Filtri di Ricerca
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Ricerca per nome */}
                <div className="space-y-2">
                  <Label htmlFor="search">Nome Locale</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Cerca per nome..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Città */}
                <div className="space-y-2">
                  <Label>Città</Label>
                  <Select value={selectedCity} onValueChange={setSelectedCity}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Cucina */}
                <div className="space-y-2">
                  <Label>Tipo di Cucina</Label>
                  <Select value={selectedCuisine} onValueChange={setSelectedCuisine}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cuisines.map(cuisine => (
                        <SelectItem key={cuisine} value={cuisine}>{cuisine}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Fascia di Prezzo */}
                <div className="space-y-2">
                  <Label>Fascia di Prezzo</Label>
                  <Select value={selectedPriceRange} onValueChange={setSelectedPriceRange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priceRanges.map(range => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* BushiScore Range */}
                <div className="space-y-2">
                  <Label>BushiScore Minimo</Label>
                  <Slider
                    value={bushiScoreRange}
                    onValueChange={setBushiScoreRange}
                    max={100}
                    min={0}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{bushiScoreRange[0]}</span>
                    <span>{bushiScoreRange[1]}</span>
                  </div>
                </div>

                {/* Pilastri */}
                <div className="space-y-2">
                  <Label>Pilastri di Eccellenza</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {pillarsData.map(pillar => (
                      <div key={pillar.kanji} className="flex items-center space-x-2">
                        <Checkbox
                          id={pillar.kanji}
                          checked={selectedPillars.includes(pillar.kanji)}
                          onCheckedChange={() => handlePillarToggle(pillar.kanji)}
                        />
                        <Label htmlFor={pillar.kanji} className="text-sm font-kanji">
                          {pillar.kanji}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Badge */}
                <div className="space-y-2">
                  <Label>Badge Verificati</Label>
                  <div className="space-y-2">
                    {availableBadges.map(badge => (
                      <div key={badge} className="flex items-center space-x-2">
                        <Checkbox
                          id={badge}
                          checked={selectedBadges.includes(badge)}
                          onCheckedChange={() => handleBadgeToggle(badge)}
                        />
                        <Label htmlFor={badge} className="text-xs">
                          {badge}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reset Filtri */}
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCity('Tutte');
                    setSelectedCuisine('Tutte');
                    setSelectedPriceRange('Tutte');
                    setBushiScoreRange([0, 100]);
                    setSelectedPillars([]);
                    setSelectedBadges([]);
                  }}
                >
                  Reset Filtri
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Risultati */}
          <div className="lg:col-span-3">
            {/* Header Risultati */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold">
                  {sortedRestaurants.length} Locali Trovati
                </h2>
                <p className="text-muted-foreground">
                  Ordinati per {sortBy === 'bushiScore' ? 'BushiScore' : sortBy}
                </p>
              </div>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bushiScore">BushiScore</SelectItem>
                  <SelectItem value="reviews">Numero Recensioni</SelectItem>
                  <SelectItem value="price">Prezzo</SelectItem>
                  <SelectItem value="name">Nome</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Lista Ristoranti */}
            <div className="space-y-6">
              {sortedRestaurants.map(restaurant => (
                <Card key={restaurant.id} className="pillar-card overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                    {/* Immagine */}
                    <div className="md:col-span-1">
                      <div className="h-48 md:h-full bg-muted flex items-center justify-center">
                        <Utensils className="h-12 w-12 text-muted-foreground" />
                      </div>
                    </div>
                    
                    {/* Contenuto */}
                    <div className="md:col-span-2 p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-bushido text-xl font-bold mb-1">
                            {restaurant.name}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            <div className="flex items-center">
                              <MapPin className="mr-1 h-4 w-4" />
                              {restaurant.city}
                            </div>
                            <span>{restaurant.cuisine}</span>
                            <span>{getPriceRangeSymbol(restaurant.priceRange)}</span>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <Badge className={`text-lg px-3 py-1 ${getBushiScoreColor(restaurant.bushiScore)}`}>
                            {restaurant.bushiScore}/100
                          </Badge>
                          <div className="text-xs text-muted-foreground mt-1">
                            BushiScore
                          </div>
                        </div>
                      </div>

                      {/* Pilastri */}
                      <div className="mb-4">
                        <h4 className="text-sm font-medium mb-2">Pilastri di Eccellenza</h4>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(restaurant.pillars).map(([kanji, score]) => (
                            score >= 4 && (
                              <Badge key={kanji} variant="secondary" className="font-kanji">
                                {kanji} {score}/5
                              </Badge>
                            )
                          ))}
                        </div>
                      </div>

                      {/* Badge */}
                      <div className="mb-4">
                        <h4 className="text-sm font-medium mb-2">Badge Verificati</h4>
                        <div className="flex flex-wrap gap-2">
                          {restaurant.badges.map(badge => (
                            <Badge key={badge} variant="outline" className="text-xs">
                              <Award className="mr-1 h-3 w-3" />
                              {badge}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <div className="flex items-center">
                            <Star className="mr-1 h-4 w-4" />
                            {restaurant.reviews} recensioni
                          </div>
                          <div>
                            Prezzo medio: €{restaurant.avgPrice}
                          </div>
                        </div>
                        
                        <Button className="btn-samurai">
                          Vedi Dettagli
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Nessun risultato */}
            {sortedRestaurants.length === 0 && (
              <Card className="text-center py-12">
                <CardContent>
                  <Search className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Nessun locale trovato</h3>
                  <p className="text-muted-foreground mb-4">
                    Prova a modificare i filtri di ricerca per trovare più risultati.
                  </p>
                  <Button variant="outline">
                    Reset Filtri
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}